import { createContext, useContext, useReducer } from 'react'
import {
  IVideosState,
  TVideosAction,
  IVideo,
  IVideosProviderProps
} from './interfaces'
import { createNewVideo } from './helpers'
import { useInitialFetch } from '../hooks/useInitialFetch'

const VideosContext = createContext<
  | {
      state: IVideosState
      dispatch: React.Dispatch<TVideosAction>
    }
  | undefined
>(undefined)

const videosReducer = (
  state: IVideosState,
  action: TVideosAction
): IVideosState => {
  switch (action.type) {
    case 'ADD_VIDEO':
      return {
        ...state,
        videos: [
          ...state.videos,
          createNewVideo(state.styles[0]?.id || 0, state.models[0]?.model)
        ]
      }

    case 'REMOVE_VIDEO':
      // Don't allow removing the last video
      if (state.videos.length <= 1) return state
      return {
        ...state,
        videos: state.videos.filter(video => video.id !== action.payload.id)
      }

    case 'UPDATE_VIDEO':
      return {
        ...state,
        videos: state.videos.map(video =>
          video.id === action.payload.id
            ? { ...video, ...action.payload.updates }
            : video
        )
      }

    case 'GENERATE_ALL':
      return state

    case 'SET_STYLES':
      return {
        ...state,
        styles: action.payload
      }

    case 'SET_MODELS':
      return {
        ...state,
        models: action.payload
      }

    case 'SET_INITIALIZING':
      return {
        ...state,
        isInitializing: action.payload
      }

    case 'UPDATE_VIDEO_BY_HASH':
      return {
        ...state,
        videos: state.videos.map(video =>
          video.genAiVideoHash === action.payload.genAiVideoHash
            ? { ...video, ...action.payload.updates }
            : video
        )
      }

    case 'UPDATE_VIDEO_BY_VIDEO_ID':
      return {
        ...state,
        videos: state.videos.map(video =>
          video.videoId === action.payload.videoId
            ? { ...video, ...action.payload.updates }
            : video
        )
      }

    case 'ADD_VIDEOS_FROM_CSV':
      return {
        ...state,
        videos: action.payload.videos
      }

    default:
      return state
  }
}

const initialState: IVideosState = {
  videos: [createNewVideo()],
  styles: [],
  models: [],
  isInitializing: true
}

export const VideosProvider = ({ children }: IVideosProviderProps) => {
  const [state, dispatch] = useReducer(videosReducer, initialState)

  useInitialFetch(dispatch)

  return (
    <VideosContext.Provider value={{ state, dispatch }}>
      {children}
    </VideosContext.Provider>
  )
}

export const useVideosContext = () => {
  const context = useContext(VideosContext)
  if (!context) {
    throw new Error('useVideosContext must be used within VideosProvider')
  }
  return context
}

export const hasVideosInProgress = (videos: IVideo[]): boolean => {
  return videos.some(video => {
    const isGenerating =
      video.generationState === 'generating' ||
      video.generationState === 'preparing_media' ||
      video.generationState === 'preparing_render'

    const isRendering =
      video.renderState === 'preparing' ||
      video.renderState === 'rendering' ||
      video.renderState === 'uploading'

    return isGenerating || isRendering
  })
}
